import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { LabTestView } from '@wodi/shared';
import {
  Button,
  Cell,
  Checkbox,
  Column,
  EmptyState,
  LoadingBlock,
  Notice,
  RadioGroup,
  Row,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextArea,
  TextField,
  useShortcut,
  Section,
} from '@wodi/ui';
import { useState, type ReactNode } from 'react';

import {
  labAnalytesQuery,
  labKeys,
  labSectionsQuery,
  labTestQuery,
  labTestsQuery,
  labValueSetQuery,
  putLabTest,
} from '../api/lab.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import styles from '../lab/lab.module.css';
import { screen } from '../layout/screen.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import { SettingsPage } from './settings-nav.tsx';

// Settings > Lab (docs/plans/phase1-lab.md §8.1): the tests the lab offers, the analytes
// they measure, the lab's own lists and the facility's lab settings. Ranges are the
// clinical lead's, on each analyte's page; nothing here invents one.

/** The Lab settings pages, under the Settings links. */
export function LabSettingsFrame({ title, children }: { title: string; children: ReactNode }) {
  return (
    <SettingsPage title={title}>
      <nav aria-label="Lab settings" className={screen.tabs}>
        <Link
          to="/settings/lab"
          activeOptions={{ exact: true }}
          activeProps={{ 'aria-current': 'page' }}
        >
          Tests
        </Link>
        <Link to="/settings/lab/analytes" activeProps={{ 'aria-current': 'page' }}>
          Analytes and ranges
        </Link>
        <Link to="/settings/lab/lists" activeProps={{ 'aria-current': 'page' }}>
          Sections and lists
        </Link>
        <Link to="/settings/lab/settings" activeProps={{ 'aria-current': 'page' }}>
          Facility settings
        </Link>
        <Link to="/settings/lab/analyzers" activeProps={{ 'aria-current': 'page' }}>
          Analyzers
        </Link>
        <Link to="/settings/lab/qc" activeProps={{ 'aria-current': 'page' }}>
          Quality control
        </Link>
        <Link to="/settings/lab/micro" activeProps={{ 'aria-current': 'page' }}>
          Microbiology lists
        </Link>
      </nav>
      {children}
    </SettingsPage>
  );
}

/** Loading and error states for a settings query, the same on every Lab settings page. */
export function QueryState({
  query,
  what,
}: {
  query: { isPending: boolean; isError: boolean; error: unknown; refetch: () => unknown };
  what: string;
}) {
  if (query.isPending) return <LoadingBlock label={`Loading ${what}`} rows={3} />;
  if (query.isError)
    return (
      <Notice
        tone="error"
        action={
          <Button dense onPress={() => void query.refetch()}>
            Try again
          </Button>
        }
      >
        {errorText(query.error, `The ${what} couldn't be loaded. Try again.`)}
      </Notice>
    );
  return null;
}

function stateOf(test: LabTestView): { word: string; tag: 'done' | 'pending' } {
  if (test.definition === null) return { word: 'Not set up for the lab', tag: 'pending' };
  return test.definition.setupState === 'ready'
    ? { word: 'Ready', tag: 'done' }
    : { word: 'Draft', tag: 'pending' };
}

/** Every lab_test item in the catalogue; priced items can't be ordered until defined here. */
export function LabTestsPage() {
  const testsQuery = useQuery(labTestsQuery);
  return (
    <LabSettingsFrame title="Lab">
      <QueryState query={testsQuery} what="lab tests" />
      {testsQuery.isSuccess && testsQuery.data.length === 0 ? (
        <EmptyState>
          No lab tests in the catalogue yet. Add them with the category Lab test in Settings,
          Services and prices, then set them up here.
        </EmptyState>
      ) : null}
      {testsQuery.isSuccess && testsQuery.data.length > 0 ? (
        <Table aria-label="Lab tests" dense>
          <TableHeader>
            <Column>Code</Column>
            <Column isRowHeader>Test</Column>
            <Column>Section</Column>
            <Column>Specimen</Column>
            <Column numeric>Components</Column>
            <Column>State</Column>
          </TableHeader>
          <TableBody>
            {testsQuery.data.map((test) => {
              const state = stateOf(test);
              return (
                <Row key={test.serviceItemId}>
                  <Cell>
                    <span className="wodi-id">{test.code}</span>
                  </Cell>
                  <Cell>
                    <Link
                      to="/settings/lab/tests/$serviceItemId"
                      params={{ serviceItemId: test.serviceItemId }}
                    >
                      {test.name}
                    </Link>
                  </Cell>
                  <Cell>{test.definition?.section.name ?? ''}</Cell>
                  <Cell>{test.definition?.specimenType.display ?? ''}</Cell>
                  <Cell numeric>{test.definition?.components.length ?? 0}</Cell>
                  <Cell>
                    <Tag state={state.tag}>{state.word}</Tag>
                  </Cell>
                </Row>
              );
            })}
          </TableBody>
        </Table>
      ) : null}
    </LabSettingsFrame>
  );
}

const editorRoute = getRouteApi('/_app/settings/lab/tests/$serviceItemId');

export function LabTestEditorPage() {
  const { serviceItemId } = editorRoute.useParams();
  const test = useQuery(labTestQuery(serviceItemId));
  return (
    <LabSettingsFrame title={test.data?.name ?? 'Lab test'}>
      <QueryState query={test} what="lab test" />
      {test.isSuccess ? (
        <TestEditor key={test.data.definition?.version ?? 0} test={test.data} />
      ) : null}
    </LabSettingsFrame>
  );
}

interface ComponentDraft {
  analyteId: string;
  heading: string;
  required: boolean;
}

function TestEditor({ test }: { test: LabTestView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const sections = useQuery(labSectionsQuery);
  const specimens = useQuery(labValueSetQuery('lab_specimen_type'));
  const containers = useQuery(labValueSetQuery('lab_container'));
  const analytes = useQuery(labAnalytesQuery);
  const d = test.definition;
  const [sectionId, setSectionId] = useState<string | null>(d?.section.id ?? null);
  const [specimen, setSpecimen] = useState<string | null>(d?.specimenType.code ?? null);
  const [container, setContainer] = useState<string | null>(d?.container.code ?? null);
  const [reportNote, setReportNote] = useState(d?.reportNote ?? '');
  const [target, setTarget] = useState(
    d?.turnaroundTargetMinutes === null || d === null ? '' : String(d.turnaroundTargetMinutes),
  );
  const [ready, setReady] = useState(d?.setupState === 'ready');
  const [workflow, setWorkflow] = useState<'standard' | 'culture'>(d?.workflow ?? 'standard');
  const [components, setComponents] = useState<ComponentDraft[]>(
    (d?.components ?? []).map((c) => ({
      analyteId: c.analyteId,
      heading: c.heading ?? '',
      required: c.required,
    })),
  );
  const [adding, setAdding] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const canEdit = session !== null && can(session, 'lab.catalogue.manage');

  const targetMinutes = target.trim() === '' ? null : Number(target.trim());
  const targetError =
    targetMinutes !== null && (!Number.isInteger(targetMinutes) || targetMinutes < 1)
      ? 'Enter whole minutes, or leave empty for no target.'
      : undefined;
  const readyError =
    ready && components.length === 0 && workflow !== 'culture'
      ? 'Add at least one component before marking the test ready.'
      : undefined;

  const save = useMutation({
    mutationFn: () =>
      putLabTest(test.serviceItemId, {
        expectedVersion: d?.version ?? null,
        sectionId: sectionId ?? '',
        specimenTypeCode: specimen ?? '',
        containerCode: container ?? '',
        reportNote: reportNote.trim() === '' ? null : reportNote.trim(),
        turnaroundTargetMinutes: targetMinutes,
        components: components.map((c) => ({
          analyteId: c.analyteId,
          heading: c.heading.trim() === '' ? null : c.heading.trim(),
          required: c.required,
        })),
        ready,
        workflow,
      }),
    onSuccess: async () => {
      toasts.show({ title: 'Lab test saved' });
      await queryClient.invalidateQueries({ queryKey: labKeys.catalogue });
    },
  });
  const go = () => {
    setTried(true);
    if (
      sectionId === null ||
      specimen === null ||
      container === null ||
      targetError !== undefined ||
      readyError !== undefined
    )
      return;
    save.mutate();
  };
  useShortcut('mod+enter', go, { allowInFields: true, enabled: canEdit });

  const nameOf = (analyteId: string) =>
    analytes.data?.find((a) => a.id === analyteId)?.name ?? 'Analyte';
  const move = (index: number, by: -1 | 1) => {
    setComponents((list) => {
      const next = [...list];
      const [item] = next.splice(index, 1);
      if (item !== undefined) next.splice(index + by, 0, item);
      return next;
    });
  };
  const available = (analytes.data ?? []).filter(
    (a) => a.status === 'active' && !components.some((c) => c.analyteId === a.id),
  );

  return (
    <form
      className={styles.form}
      aria-label="Lab test definition"
      onSubmit={(event) => {
        event.preventDefault();
        go();
      }}
    >
      <p className={styles.meta}>
        Code <span className="wodi-id">{test.code}</span>. The price and SHA code are in Settings,
        Services and prices.
      </p>
      {sections.isError || specimens.isError || containers.isError || analytes.isError ? (
        <Notice tone="error">
          The lab's lists couldn't be loaded. Reload the page, or check Sections and lists.
        </Notice>
      ) : null}
      <Select
        label="Section"
        options={(sections.data ?? [])
          .filter((s) => s.status === 'active' || s.id === sectionId)
          .map((s) => ({ id: s.id, label: s.name }))}
        value={sectionId}
        onChange={(v) => {
          setSectionId(v === null ? null : String(v));
        }}
        isDisabled={!canEdit}
        {...(tried && sectionId === null ? { errorMessage: 'Choose the section.' } : {})}
      />
      <div className={styles.row}>
        <Select
          label="Specimen"
          options={(specimens.data ?? [])
            .filter((s) => s.status === 'active' || s.code === specimen)
            .map((s) => ({ id: s.code, label: s.display }))}
          value={specimen}
          onChange={(v) => {
            setSpecimen(v === null ? null : String(v));
          }}
          isDisabled={!canEdit}
          {...(tried && specimen === null ? { errorMessage: 'Choose the specimen.' } : {})}
        />
        <Select
          label="Container"
          options={(containers.data ?? [])
            .filter((s) => s.status === 'active' || s.code === container)
            .map((s) => ({ id: s.code, label: s.display }))}
          value={container}
          onChange={(v) => {
            setContainer(v === null ? null : String(v));
          }}
          isDisabled={!canEdit}
          {...(tried && container === null ? { errorMessage: 'Choose the container.' } : {})}
        />
      </div>
      <Section title="Components, in report order">
        {components.length === 0 ? (
          <p className={styles.meta}>No components yet. A test needs at least one to be ready.</p>
        ) : null}
        <ol className={styles.list}>
          {components.map((c, index) => (
            <li key={c.analyteId} className={styles.listRow}>
              <strong>{nameOf(c.analyteId)}</strong>
              <span className={styles.row}>
                <TextField
                  label={`Heading above ${nameOf(c.analyteId)} (optional)`}
                  dense
                  value={c.heading}
                  isDisabled={!canEdit}
                  onChange={(heading) => {
                    setComponents((list) =>
                      list.map((x, i) => (i === index ? { ...x, heading } : x)),
                    );
                  }}
                />
                <Checkbox
                  isSelected={c.required}
                  isDisabled={!canEdit}
                  onChange={(required) => {
                    setComponents((list) =>
                      list.map((x, i) => (i === index ? { ...x, required } : x)),
                    );
                  }}
                >
                  Required
                </Checkbox>
              </span>
              {canEdit ? (
                <span className={styles.actions}>
                  <Button
                    dense
                    variant="quiet"
                    isDisabled={index === 0}
                    aria-label={`Move ${nameOf(c.analyteId)} up`}
                    onPress={() => {
                      move(index, -1);
                    }}
                  >
                    Up
                  </Button>
                  <Button
                    dense
                    variant="quiet"
                    isDisabled={index === components.length - 1}
                    aria-label={`Move ${nameOf(c.analyteId)} down`}
                    onPress={() => {
                      move(index, 1);
                    }}
                  >
                    Down
                  </Button>
                  <Button
                    dense
                    variant="quiet"
                    aria-label={`Remove ${nameOf(c.analyteId)}`}
                    onPress={() => {
                      setComponents((list) => list.filter((_, i) => i !== index));
                    }}
                  >
                    Remove
                  </Button>
                </span>
              ) : (
                <span />
              )}
            </li>
          ))}
        </ol>
        {canEdit ? (
          <div className={styles.toolbar}>
            <Select
              label="Add a component"
              options={available.map((a) => ({ id: a.id, label: a.name }))}
              value={adding}
              onChange={(v) => {
                setAdding(v === null ? null : String(v));
              }}
              description="Analytes are set up under Analytes and ranges."
            />
            <Button
              isDisabled={adding === null}
              onPress={() => {
                if (adding === null) return;
                setComponents((list) => [
                  ...list,
                  { analyteId: adding, heading: '', required: true },
                ]);
                setAdding(null);
              }}
            >
              Add component
            </Button>
          </div>
        ) : null}
      </Section>
      <RadioGroup
        label="Workflow"
        orientation="horizontal"
        value={workflow}
        onChange={(value) => {
          setWorkflow(value === 'culture' ? 'culture' : 'standard');
        }}
        isDisabled={!canEdit}
        description="A culture records organisms and sensitivities; components are optional."
        options={[
          { id: 'standard', label: 'Standard' },
          { id: 'culture', label: 'Culture and sensitivity' },
        ]}
      />
      <TextArea
        label="Report note (optional)"
        description="Printed under the test, for example the method. Not clinical advice."
        value={reportNote}
        onChange={setReportNote}
        isDisabled={!canEdit}
        rows={2}
      />
      <TextField
        label="Turnaround target (optional)"
        unit="min"
        description='The worklist shows "Over target" after this long.'
        value={target}
        onChange={setTarget}
        isDisabled={!canEdit}
        {...(targetError === undefined ? {} : { errorMessage: targetError })}
      />
      <Checkbox
        isSelected={ready}
        onChange={setReady}
        isDisabled={!canEdit}
        description="Only ready tests can be requested."
        {...(tried && readyError !== undefined ? { errorMessage: readyError } : {})}
      >
        Ready to order
      </Checkbox>
      {save.isError ? (
        <Notice tone="error">
          {errorText(save.error, "The lab test wasn't saved. Try again.")}
        </Notice>
      ) : null}
      {canEdit ? (
        <div className={styles.actions}>
          <Button
            type="submit"
            variant="primary"
            isPending={save.isPending}
            pendingLabel="Saving…"
            isDisabled={!online}
          >
            Save test
          </Button>
        </div>
      ) : (
        <Notice tone="info">Only the lab manager can change lab tests.</Notice>
      )}
    </form>
  );
}
